import { useCallback, useMemo, useState } from "react";
import { useDataProvider, useGetOne, useNotify, usePermissions, useRedirect } from "react-admin";
import {
  Views,
  type View,
  type ToolbarProps,
  type EventPropGetter,
  type SlotInfo,
} from "react-big-calendar";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { formatIsoDate, formatListDate, formatWeekdayFull } from "@/utils/date-format";
import { toUtcYmd } from "@/utils/hall-slot-matches-raza-event-date";
import { hasPermission } from "@/utils/permission-utils";
import { ResourceBigCalendarView } from "@/components/resource-big-calendar/ResourceBigCalendarView";
import { parseCalendarViewFromSearchParam } from "@/components/resource-big-calendar/resourceCalendarTimeGridDefaults";
import { useSyncResourceCalendarSearchParams } from "@/components/resource-big-calendar/useSyncResourceCalendarSearchParams";
import { useRouteId } from "@/utils/route-utility";
import type { HouseVisitEventRecord } from "../types";
import { HouseVisitSlotCalendarEventComponent } from "./slot-calendar/HouseVisitSlotCalendarEventComponent";
import { HouseVisitSlotsCalendarToolbar } from "./slot-calendar/HouseVisitSlotsCalendarToolbar";
import { HouseVisitSlotsMonthDateHeader } from "./slot-calendar/HouseVisitSlotsMonthDateHeader";
import { houseVisitSlotEventShellStyle } from "./slot-calendar/slot-calendar-colors";
import { useHouseVisitSlotsCalendarEvents } from "./slot-calendar/use-house-visit-slots-calendar-events";
import type { HouseVisitSlotCalendarEvent } from "./slot-calendar/types";

function isSundayYmd(ymd: string): boolean {
  return dayjs(ymd).day() === 0;
}

export const HouseVisitSlotsCalendarView = () => {
  const eventId = useRouteId();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"), { noSsr: true });

  const canCreate = hasPermission(permissions, "houseVisitSlots.create");

  const [searchParams, setSearchParams] = useSearchParams();
  useSyncResourceCalendarSearchParams(isMobile);

  const view: View = parseCalendarViewFromSearchParam(searchParams.get("view"), isMobile);
  const dateParam = searchParams.get("date");
  const date: Dayjs = dateParam && dayjs(dateParam).isValid() ? dayjs(dateParam) : dayjs();

  const { events, reload } = useHouseVisitSlotsCalendarEvents(view, date, eventId);
  const { data: eventRecord } = useGetOne<HouseVisitEventRecord>(
    "houseVisitEvents",
    { id: eventId ?? "" },
    { enabled: Boolean(eventId) }
  );

  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleViewChange = useCallback(
    (newView: View) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", String(newView));
        return next;
      });
    },
    [setSearchParams]
  );

  const handleDateChange = useCallback(
    (newDate: Dayjs) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("date", formatIsoDate(newDate));
        return next;
      });
    },
    [setSearchParams]
  );

  const handleSelectEvent = useCallback(
    (event: HouseVisitSlotCalendarEvent) => {
      if (event?.id != null) {
        redirect("show", "houseVisitSlots", event.id);
      }
    },
    [redirect]
  );

  const openExistingOrPromptCreate = useCallback(
    (ymd: string) => {
      const existing = events.find((e) => toUtcYmd(e.resource.date) === ymd);
      if (existing?.id != null) {
        redirect("show", "houseVisitSlots", existing.id);
        return;
      }

      if (!canCreate) {
        notify("No slot on this date. You need permission to create slots.", { type: "warning" });
        return;
      }

      if (isSundayYmd(ymd)) {
        notify("Sunday dates are not allowed for house visit slots", { type: "warning" });
        return;
      }

      const fromYmd = eventRecord?.fromDate ? toUtcYmd(eventRecord.fromDate) : null;
      const toYmd = eventRecord?.toDate ? toUtcYmd(eventRecord.toDate) : null;
      if (fromYmd && ymd < fromYmd) {
        notify(`Date is before the event start (${formatListDate(fromYmd, { utc: true })})`, {
          type: "warning",
        });
        return;
      }
      if (toYmd && ymd > toYmd) {
        notify(`Date is after the event end (${formatListDate(toYmd, { utc: true })})`, {
          type: "warning",
        });
        return;
      }

      setPendingDate(ymd);
    },
    [canCreate, eventRecord?.fromDate, eventRecord?.toDate, events, notify, redirect]
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      const ymd = formatIsoDate(dayjs(slotInfo.start));
      if (!ymd) return;
      openExistingOrPromptCreate(ymd);
    },
    [openExistingOrPromptCreate]
  );

  const createSlot = async () => {
    if (!eventId || !pendingDate) return;
    setCreating(true);
    try {
      const { data } = await dataProvider.create("houseVisitSlots", {
        data: {
          eventId,
          date: pendingDate,
          label: "",
          capacity: 10,
        },
      });
      setPendingDate(null);
      notify("Slot created", { type: "info" });
      void reload();
      if (data?.id != null) {
        redirect("show", "houseVisitSlots", data.id);
      }
    } catch (err: unknown) {
      const message =
        (err as { body?: { message?: string } })?.body?.message ||
        (err as Error)?.message ||
        "Failed to create slot";
      notify(message, { type: "warning" });
    } finally {
      setCreating(false);
    }
  };

  const renderToolbar = useCallback(
    (toolbarProps: ToolbarProps) => (
      <HouseVisitSlotsCalendarToolbar
        {...toolbarProps}
        selectedDate={date}
        setSelectedDate={handleDateChange}
      />
    ),
    [date, handleDateChange]
  );

  const eventPropGetter = useMemo(
    () =>
      ((event: HouseVisitSlotCalendarEvent) => ({
        style: {
          ...houseVisitSlotEventShellStyle(muiTheme, event),
          ...(view === Views.MONTH
            ? {
                padding: "1px 3px",
                fontSize: "0.7rem",
                lineHeight: 1.15,
                minHeight: 0,
              }
            : {
                padding: "3px 5px",
                fontSize: "0.75rem",
                lineHeight: 1.2,
              }),
        },
      })) as EventPropGetter<HouseVisitSlotCalendarEvent>,
    [muiTheme, view]
  );

  if (!eventId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Open a house visit event to plan slots.</Typography>
      </Box>
    );
  }

  return (
    <>
      <ResourceBigCalendarView<HouseVisitSlotCalendarEvent>
        events={events}
        view={view}
        onViewChange={handleViewChange}
        date={date}
        onDateChange={handleDateChange}
        onSelectEvent={handleSelectEvent}
        selectable
        onSelectSlot={handleSelectSlot}
        EventComponent={HouseVisitSlotCalendarEventComponent}
        renderToolbar={renderToolbar}
        monthDateHeader={HouseVisitSlotsMonthDateHeader}
        eventPropGetter={eventPropGetter}
      />

      <Dialog
        open={Boolean(pendingDate)}
        onClose={() => (!creating ? setPendingDate(null) : undefined)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create slot?</DialogTitle>
        <DialogContent>
          {pendingDate ? (
            <Typography variant="body2" color="text.secondary">
              Add a house-visit slot for{" "}
              <Box component="span" fontWeight={700} color="text.primary">
                {formatWeekdayFull(pendingDate, { utc: true })} ·{" "}
                {formatListDate(pendingDate, { utc: true })}
              </Box>
              ?
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setPendingDate(null)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={creating}
            onClick={() => {
              void createSlot();
            }}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ fontWeight: 700, minWidth: 120 }}
          >
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HouseVisitSlotsCalendarView;
